import { formatInTimezone } from '../../../utils/formatInTimezone';
import { describeUserError } from '../../../utils/apiError';
import { eventTypesOf, splitIdsByType, valuesOf } from './eventsQueryModel';

/*
 * Модель блока «Возможные дубли» (№21): сборка запроса задания, параметров
 * списка кандидатов и текста строки кандидата. Без React и без запросов.
 */

// Дефолты на согласование с владельцем (spec №21): backend требует оба значения и своих дефолтов не даёт.
export const DUPLICATE_TIME_WINDOW_SECONDS = 30;
export const DUPLICATE_MAX_HAMMING_DISTANCE = 10;
export const CANDIDATES_PAGE_SIZE = 10;

export const JOB_ACTIVE_STATUSES = ['pending', 'running', 'cancel_requested'];
export const JOB_POLL_INTERVAL_MS = 2000;

const HIERARCHY_DIMENSIONS = [
  ['factory_ids', 'factory_id'],
  ['workshop_ids', 'workshop_id'],
  ['section_ids', 'section_id'],
  ['room_ids', 'room_id'],
];
const SOURCE_STATUSES = ['active', 'resolved'];

// Сколько нарушений собирается по id, когда фильтр не укладывается в source_filter.
export const DUPLICATE_MAX_COLLECTED = 1000;

export const DUPLICATES_VIOLATIONS_ONLY_NOTE = 'Дубли ищутся только среди нарушений, бизнес-события в поиск не попадают';

const nonEmptyString = (value) => (typeof value === 'string' && value !== '' ? value : undefined);
const toNumbers = (values) => values.map(Number).filter(Number.isFinite);

/**
 * source_filter задания, если фильтр в него укладывается: одно правило или
 * ни одного, один статус или ни одного, уровень иерархии выше камеры не задан,
 * а камера или период есть (поиск по всем событиям молча не запускаем).
 * Иначе null.
 */
const toSourceFilter = (filters) => {
  const cameraIds = toNumbers(valuesOf(filters, 'camera_ids', 'camera_id'));
  const rules = valuesOf(filters, 'rule_ids', 'rule_id');
  const statuses = valuesOf(filters, 'statuses', 'status').filter((status) => SOURCE_STATUSES.includes(status));
  const hierarchy = HIERARCHY_DIMENSIONS.some(([plural, single]) => valuesOf(filters, plural, single).length > 0);
  if (rules.length > 1 || statuses.length > 1) return null;
  if (cameraIds.length === 0 && hierarchy) return null;

  const sourceFilter = {};
  if (cameraIds.length > 0) sourceFilter.camera_ids = cameraIds;
  if (rules.length === 1 && nonEmptyString(rules[0])) sourceFilter.rule_id = rules[0];
  if (statuses.length === 1) sourceFilter.status = statuses[0];
  if (nonEmptyString(filters.from)) sourceFilter.confirmed_from = filters.from;
  if (nonEmptyString(filters.to)) sourceFilter.confirmed_to = filters.to;
  if (!sourceFilter.camera_ids && !sourceFilter.confirmed_from && !sourceFilter.confirmed_to) return null;
  return sourceFilter;
};

/**
 * Тело POST /duplicate-analysis/jobs. Дубли ищутся только среди нарушений.
 *
 * ids — отмеченные нарушения, нужно минимум два.
 * filters — source_filter, если фильтр в него укладывается и в нём только
 * нарушения. Иначе (несколько правил, объект без камеры, бизнес-события в
 * фильтре) нарушения выборки собираются списком по id: `deferred` и
 * `listParams` — для collectEventIds, тело собирает withCollectedIds.
 *
 * Возвращает { available, deferred, reason, note, body, listParams }.
 */
export const buildDuplicateJobRequest = ({ mode, ids, types, filters }) => {
  const limits = {
    time_window_seconds: DUPLICATE_TIME_WINDOW_SECONDS,
    max_hamming_distance: DUPLICATE_MAX_HAMMING_DISTANCE,
  };
  const base = { available: false, deferred: false, reason: '', note: '', body: null, listParams: null };

  if (mode === 'filters') {
    const source = filters || {};
    const categories = eventTypesOf(source);
    if (!categories.includes('violation')) {
      return { ...base, reason: 'Дубли ищутся только среди нарушений. Отметьте тип «Нарушение» в фильтре' };
    }
    const note = categories.length > 1 ? DUPLICATES_VIOLATIONS_ONLY_NOTE : '';
    const sourceFilter = categories.length === 1 ? toSourceFilter(source) : null;
    if (sourceFilter) {
      return { ...base, available: true, note, body: { source_filter: sourceFilter, ...limits } };
    }
    return {
      ...base,
      available: true,
      deferred: true,
      note,
      listParams: { ...source, event_types: ['violation'] },
    };
  }

  const byType = splitIdsByType(ids, types);
  const list = [...new Set(byType.violation)];
  const note = byType.business.length > 0 ? DUPLICATES_VIOLATIONS_ONLY_NOTE : '';
  if (list.length < 2) {
    return {
      ...base,
      note,
      reason: byType.business.length > 0 ? 'Отметьте хотя бы два нарушения' : 'Отметьте хотя бы два события',
    };
  }
  return { ...base, available: true, note, body: { event_ids: list, ...limits } };
};

/** Собранные id нарушений → тело задания или причина, почему его нет. */
export const withCollectedIds = (collected) => {
  if (collected === null) {
    return {
      available: false,
      reason: `В выборе больше ${DUPLICATE_MAX_COLLECTED} нарушений, сузьте фильтр для поиска дублей`,
      body: null,
    };
  }
  if (collected.length < 2) {
    return { available: false, reason: 'В выборе меньше двух нарушений', body: null };
  }
  return {
    available: true,
    reason: '',
    body: {
      event_ids: collected,
      time_window_seconds: DUPLICATE_TIME_WINDOW_SECONDS,
      max_hamming_distance: DUPLICATE_MAX_HAMMING_DISTANCE,
    },
  };
};

/**
 * Параметры списка кандидатов и сводки: камера, правило и период из фильтра.
 * Список принимает одну камеру и одно правило — при нескольких поле не
 * отправляется. В режиме ids фильтра нет — берутся все кандидаты.
 */
export const buildCandidateParams = ({ mode, filters }) => {
  if (mode !== 'filters') return {};
  const source = filters || {};
  const params = {};
  const cameraIds = toNumbers(valuesOf(source, 'camera_ids', 'camera_id'));
  const rules = valuesOf(source, 'rule_ids', 'rule_id');
  if (cameraIds.length === 1) params.camera_id = cameraIds[0];
  if (rules.length === 1 && nonEmptyString(rules[0])) params.rule_id = rules[0];
  if (nonEmptyString(source.from)) params.confirmed_from = source.from;
  if (nonEmptyString(source.to)) params.confirmed_to = source.to;
  return params;
};

/** «Точность по проверке»: null — «—». */
export const formatReviewPrecision = (percent) => {
  if (percent === null || percent === undefined || !Number.isFinite(Number(percent))) return '—';
  return `${String(Number(Number(percent).toFixed(1))).replace('.', ',')}%`;
};

/**
 * Строка кандидата: «#2993 и #2994 · Касса · Счёт №3 · разница 4 с» + время подтверждения.
 * Незнакомые камера или правило в строку не попадают.
 */
export const describeCandidate = (candidate, { cameraName, ruleName, timezone } = {}) => {
  const parts = [`#${candidate.reference_event_id} и #${candidate.candidate_event_id}`];
  if (cameraName) parts.push(cameraName);
  if (ruleName) parts.push(ruleName);
  const distance = Number(candidate.time_distance_seconds);
  if (Number.isFinite(distance)) parts.push(`разница ${distance} с`);
  const time = candidate.reference_confirmed_at
    ? formatInTimezone(candidate.reference_confirmed_at, timezone)
    : '';
  return { title: parts.join(' · '), time };
};

/*
 * FE-CORR-07 DUP-001: запуск поиска разрешают только capabilities backend.
 * Флаг DUPLICATES01 frontend не включает и не обходит: пока capabilities не
 * загрузились, не пришли или запрещают создание задания, кнопка недоступна
 * (fail-closed). Просмотр сохранённых пар от этого не зависит — он закрыт
 * только явным review_enabled=false.
 */
export const DUPLICATES_DISABLED_CODE = 'DUPLICATES01_DISABLED';
export const DUPLICATES_UNAVAILABLE_TEXT = 'Автоматический поиск дубликатов пока недоступен';
export const DUPLICATES_FORBIDDEN_TEXT = 'Нет доступа к поиску дубликатов';
export const DUPLICATES_CAPABILITIES_ERROR_TEXT =
  'Не удалось проверить, доступен ли поиск дубликатов. Обновите страницу позже';

const errorCode = (error) => {
  const data = error?.response?.data;
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.code || detail?.error_code || data?.code || data?.error_code || null;
};

export const isDuplicatesDisabledError = (error) =>
  error?.response?.status === 503 && errorCode(error) === DUPLICATES_DISABLED_CODE;

const isAccessError = (error) => [401, 403].includes(error?.response?.status);

/**
 * capabilities: { state: 'loading' | 'ready' | 'error', data, error }.
 * Возвращает, можно ли создать задание, и одну причину, если нельзя.
 */
export const describeDuplicateCapabilities = (capabilities) => {
  if (!capabilities || capabilities.state === 'loading') {
    return { canCreate: false, reason: null, reviewEnabled: true };
  }
  if (capabilities.state === 'error') {
    return {
      canCreate: false,
      reason: isAccessError(capabilities.error) ? DUPLICATES_FORBIDDEN_TEXT : DUPLICATES_CAPABILITIES_ERROR_TEXT,
      reviewEnabled: true,
    };
  }
  const data = capabilities.data || {};
  const canCreate = data.job_creation_enabled === true;
  return {
    canCreate,
    reason: canCreate ? null : DUPLICATES_UNAVAILABLE_TEXT,
    reviewEnabled: data.review_enabled !== false,
  };
};

/** Текст ошибки POST /duplicate-analysis/jobs; 503-выключение обрабатывается отдельно. */
export const describeStartError = (error) => {
  const status = error?.response?.status;
  if (isAccessError(error)) return DUPLICATES_FORBIDDEN_TEXT;
  if (status === 422) {
    return describeUserError(error, 'Выбранные события нельзя проверить на дубли. Измените выбор и попробуйте снова');
  }
  return describeUserError(error, 'Не удалось запустить поиск дублей');
};
